/**
 * DraftableField — a textarea (or input) that tracks unapplied user edits.
 *
 * Behaviour:
 *  - Shows the committed value (from the Story/World/Character Bible).
 *  - When the user edits and blurs, calls `onDraftSave(draftValue)`.
 *  - If a `draftValue` is provided (loaded from DB), shows a yellow
 *    "UNAPPLIED" ribbon and populates the field with the draft instead.
 *  - "Apply" button calls `onApply()` — promotes the draft to committed
 *    and adds to the change_cue.
 *  - "Discard" button calls `onDiscard()` — reverts to committed.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Loader2 } from 'lucide-react';

/**
 * @param {object} props
 * @param {string}   props.label          — field label shown above
 * @param {string}   [props.committedValue] — saved/committed value from DB
 * @param {string}   [props.draftValue]   — unapplied user draft (from DB)
 * @param {boolean}  [props.multiline]    — use Textarea (default) or Input
 * @param {number}   [props.rows]         — textarea rows (default 4)
 * @param {string}   [props.placeholder]
 * @param {function} props.onDraftSave    — (value:string) => Promise<void>
 * @param {function} props.onApply        — () => Promise<void>
 * @param {function} props.onDiscard      — () => Promise<void>
 * @param {string}   [props.testId]
 */
export function DraftableField({
  label,
  committedValue = '',
  draftValue,
  multiline = true,
  rows = 4,
  placeholder = '',
  onDraftSave,
  onApply,
  onDiscard,
  testId,
  className,
}) {
  const hasDraft = !!draftValue;
  // Display value: draft takes precedence so the user can see their unapplied edit
  const displayValue = hasDraft ? draftValue : (committedValue || '');
  const [localValue, setLocalValue] = useState(displayValue);
  const [applying, setApplying] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const prevDraftRef = useRef(draftValue);

  // Sync when the draft changes from outside (e.g. initial DB load)
  useEffect(() => {
    if (draftValue !== prevDraftRef.current) {
      prevDraftRef.current = draftValue;
      setLocalValue(hasDraft ? draftValue : (committedValue || ''));
    }
  }, [draftValue, committedValue, hasDraft]);

  // If no draft and committedValue changes, sync local
  useEffect(() => {
    if (!hasDraft) setLocalValue(committedValue || '');
  }, [committedValue, hasDraft]);

  const handleBlur = useCallback(() => {
    if (!onDraftSave) return;
    const trimmed = localValue.trim();
    // Only save as draft if the user actually changed something
    if (trimmed === (committedValue || '').trim()) return;
    if (trimmed === (draftValue || '').trim()) return;
    onDraftSave(trimmed);
  }, [localValue, committedValue, draftValue, onDraftSave]);

  const handleApply = async () => {
    setApplying(true);
    try { await onApply?.(); } finally { setApplying(false); }
  };

  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      await onDiscard?.();
      setLocalValue(committedValue || '');
    } finally { setDiscarding(false); }
  };

  const InputEl = multiline ? Textarea : Input;

  return (
    <div data-testid={testId} className={cn('space-y-1.5', className)}>
      {/* Label row */}
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </label>
        {hasDraft && (
          <span className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-500 border border-amber-500/30">
            Unapplied
          </span>
        )}
      </div>

      {/* Field */}
      <div className={cn('relative', hasDraft && 'ring-1 ring-amber-500/50 rounded-md')}>
        <InputEl
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          rows={multiline ? rows : undefined}
          className={cn(
            'w-full text-[13px] resize-none',
            hasDraft && 'border-amber-500/40 focus-visible:ring-amber-500/40',
          )}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>

      {/* Apply / Discard buttons — only shown when there's an unapplied draft */}
      {hasDraft && (
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant="outline"
            className="h-6 px-2 text-[11px] border-amber-500/40 text-amber-600 hover:bg-amber-500/10"
            onClick={handleApply}
            disabled={applying || discarding}
            data-testid={testId ? `${testId}-apply-btn` : undefined}
          >
            {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
            Apply
          </Button>
          <Button
            size="xs"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-muted-foreground hover:text-destructive"
            onClick={handleDiscard}
            disabled={applying || discarding}
            data-testid={testId ? `${testId}-discard-btn` : undefined}
          >
            {discarding ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
            Discard
          </Button>
          <span className="text-[10px] text-muted-foreground italic">
            Writes to the narrative on next Commit & Rebuild
          </span>
        </div>
      )}
    </div>
  );
}
